import {
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  LockClosedIcon,
  ScaleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import { STRENGTHS } from '../content'
import { anim, prog } from '../motion'
import { Avatar, COLORS, Count, Shape, Typed } from '../primitives'
import { useBeats } from './beats'

/**
 * Panels der Office-Version. Bildschirm-Panels liegen über dem Diorama,
 * Welt-Panels hängen an einem Punkt im Büro und zoomen mit der Kamera.
 * Die Optik der Profile lehnt sich an die PDF-Reports an (BeamGraph,
 * At a Glance), stark vereinfacht.
 */

const range = PropTypes.arrayOf(PropTypes.number)

// Farben aus den PDF-Reports (aivy-pdf-reports/constants.js, BeamGraph.js)
const PDF = {
  track: '#EBEFFD',
  ideal: 'rgb(167,247,231)',
  transition: 'rgb(253,218,148)',
  marker: '#182033',
  highDark: '#0F6856',
  highMedium: '#4FF0D0',
  highLight: '#C7FFF4'
}

export function Panel({ at, out, place = 'right', className, children }) {
  return (
    <div className={clsx('off-panel', `off-panel--${place}`)}>
      <div
        className={clsx('off-panel__inner', className)}
        style={anim({ at, out, y: 40, oy: -30, s: 0.94 })}
      >
        {children}
      </div>
    </div>
  )
}

Panel.propTypes = {
  at: range.isRequired,
  out: range,
  place: PropTypes.oneOf(['right', 'center', 'left']),
  className: PropTypes.string,
  children: PropTypes.node
}

// ---------- 2 · HR startet mit Aivy ----------

export function OptionCards() {
  const { inB } = useBeats()
  return (
    <div className='off-options'>
      <div className='off-options__row'>
        <div
          className='off-option'
          style={anim({ at: inB('hr', 0.22, 0.36), y: 30, s: 0.7 })}
        >
          <span className='off-chip off-chip--blue'>Vorlage von Aivy</span>
          <strong>Aivy-Vorlage verwenden</strong>
          <p>
            Bewährte Anforderungen für Customer Success, basierend auf bis zu 95
            befragten HR-Expert:innen.
          </p>
        </div>
        <div
          className='off-options__plus'
          style={anim({ at: inB('hr', 0.42, 0.5), s: 0.3, y: 0 })}
        >
          +
        </div>
        <div
          className='off-option'
          style={anim({ at: inB('hr', 0.34, 0.48), y: 30, s: 0.7 })}
        >
          <span className='off-chip off-chip--green'>Fachbereiche</span>
          <strong>Kolleg:innen einladen</strong>
          <p>
            Die Menschen, die mit der neuen Person arbeiten, bringen ihre Sicht
            ein.
          </p>
        </div>
      </div>
      <div
        className='off-option off-option--combo'
        style={anim({ at: inB('hr', 0.56, 0.7), y: 30, s: 0.8 })}
      >
        <SparklesIcon width={18} height={18} />
        <div>
          <strong>Beides kombinieren</strong>
          <p>Die Vorlage ist der Startpunkt, die Fachbereiche schärfen nach.</p>
        </div>
      </div>
    </div>
  )
}

// ---------- 3 · Anforderungsanalyse ----------

export function AnalysisScreen({ delay = 0, question }) {
  const { inB } = useBeats()
  const d = delay
  return (
    <div
      className='off-analysis'
      style={anim({
        at: inB('analyse', 0.2 + d, 0.3 + d),
        out: inB('analyse', 0.88, 0.96),
        y: 20,
        s: 0.6
      })}
    >
      <div className='off-analysis__head'>Anforderungsanalyse</div>
      <div className='off-analysis__q'>{question}</div>
      <div className='off-analysis__scale'>
        <span>unwichtig</span>
        <div className='off-analysis__track'>
          <i
            style={{
              ...prog(inB('analyse', 0.32 + d, 0.52 + d)),
              left: 'calc(15% + var(--p) * 60%)'
            }}
          />
        </div>
        <span>sehr wichtig</span>
      </div>
      <div className='off-analysis__bar'>
        <i
          style={{
            ...prog(inB('analyse', 0.3 + d, 0.66 + d)),
            transform: 'scaleX(var(--p))'
          }}
        />
      </div>
      <div
        className='off-analysis__done'
        style={anim({ at: inB('analyse', 0.66 + d, 0.72 + d), s: 0.5, y: 0 })}
      >
        <CheckIcon width={12} height={12} /> Fertig
      </div>
    </div>
  )
}

AnalysisScreen.propTypes = {
  delay: PropTypes.number,
  question: PropTypes.string.isRequired
}

// ---------- 4 · Anforderungsprofil (wie im PDF, vereinfacht) ----------

const PROFILE = [
  {
    cat: 'Fähigkeiten',
    name: 'Emotionswahrnehmung',
    poles: ['niedrig', 'hoch'],
    ideal: [62, 86],
    trans: [52, 94],
    experts: [66, 74, 82],
    must: true
  },
  {
    cat: 'Fähigkeiten',
    name: 'Problemlösefähigkeit',
    poles: ['niedrig', 'hoch'],
    ideal: [44, 72],
    trans: [34, 82],
    experts: [50, 58, 66]
  },
  {
    cat: 'Persönlichkeit',
    name: 'Verträglichkeit',
    poles: ['direkt', 'umgänglich'],
    ideal: [56, 82],
    trans: [46, 90],
    experts: [60, 70, 78]
  },
  {
    cat: 'Persönlichkeit',
    name: 'Gewissenhaftigkeit',
    poles: ['niedrig', 'hoch'],
    ideal: [50, 78],
    trans: [40, 88],
    experts: [56, 64, 72]
  },
  {
    cat: 'Kultur',
    name: 'Teamarbeit oder Unabhängigkeit',
    poles: ['Teamarbeit', 'Unabhängigkeit'],
    ideal: [14, 40],
    trans: [6, 52],
    experts: [20, 28, 34]
  }
]

const EXPERT_COLORS = [COLORS.green, COLORS.yellow, '#4461D3']

export function Beam({ ideal, trans, value, experts, at }) {
  return (
    <div className='off-beam'>
      <i
        className='off-beam__trans'
        style={{
          left: `${trans[0]}%`,
          width: `${trans[1] - trans[0]}%`,
          ...(at ? { ...prog(at), transform: 'scaleX(var(--p))' } : {})
        }}
      />
      <i
        className='off-beam__ideal'
        style={{
          left: `${ideal[0]}%`,
          width: `${ideal[1] - ideal[0]}%`,
          ...(at ? { ...prog(at), transform: 'scaleX(var(--p))' } : {})
        }}
      />
      {experts?.map((v, i) => (
        <b
          key={i}
          className='off-beam__expert'
          style={{ left: `${v}%`, background: EXPERT_COLORS[i] }}
        />
      ))}
      {value != null && (
        <b className='off-beam__value' style={{ left: `${value}%` }} />
      )}
    </div>
  )
}

Beam.propTypes = {
  ideal: range.isRequired,
  trans: range.isRequired,
  value: PropTypes.number,
  experts: range,
  at: range
}

export function ProfilePanel() {
  const { inB } = useBeats()
  return (
    <Panel
      at={inB('profil', 0.04, 0.2)}
      out={inB('profil', 0.9, 1)}
      className='off-doc'
    >
      <div className='off-doc__head'>
        <div>
          <div className='off-doc__eyebrow'>Anforderungsprofil</div>
          <div className='off-doc__title'>Customer Success Manager:in</div>
        </div>
        <span className='off-chip'>DIN 33430</span>
      </div>
      <p className='off-doc__lead'>
        Von Aivy berechnet und durch deine Expert:innen gestaltet.
      </p>
      <div className='off-doc__tabs'>
        {[
          'Fähigkeiten',
          'Persönlichkeit',
          'Kultur',
          'Berufliche Interessen'
        ].map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      {PROFILE.map((d, i) => (
        <div
          key={d.name}
          className='off-dim'
          style={anim({
            at: inB('profil', 0.16 + i * 0.05, 0.3 + i * 0.05),
            x: 30,
            y: 0
          })}
        >
          <div className='off-dim__head'>
            <span>{d.name}</span>
            {d.must && <span className='off-must'>Muss</span>}
            <small>{d.cat}</small>
          </div>
          <Beam {...d} at={inB('profil', 0.24 + i * 0.05, 0.42 + i * 0.05)} />
          <div className='off-dim__poles'>
            <span>{d.poles[0]}</span>
            <span>{d.poles[1]}</span>
          </div>
        </div>
      ))}
      <div className='off-legend'>
        <span>
          <i style={{ background: PDF.ideal }} /> Idealbereich
        </span>
        <span>
          <i style={{ background: PDF.transition }} /> Übergangsbereich
        </span>
        <span>
          <b style={{ background: EXPERT_COLORS[0] }} />
          <b style={{ background: EXPERT_COLORS[1] }} />
          <b style={{ background: EXPERT_COLORS[2] }} /> Expert:innen
        </span>
      </div>
    </Panel>
  )
}

// ---------- 6 · Game-based Assessment ----------

const BENEFITS = [
  {
    Icon: SparklesIcon,
    title: 'Spielerisch statt Prüfungsstress',
    text: 'Kurze Spiele, es gibt keine guten oder schlechten Ergebnisse.'
  },
  {
    Icon: DevicePhoneMobileIcon,
    title: 'Wenige Minuten, überall',
    text: 'Am Smartphone oder im Web, wann es den Bewerbenden passt.'
  },
  {
    Icon: AcademicCapIcon,
    title: 'Wissenschaftlich fundiert',
    text: 'Entwickelt entlang der DIN 33430, misst jobrelevante Stärken.'
  }
]

const GAMES = [
  { name: 'Codeknacker', dim: 'Merkfähigkeit', kind: 'sq', color: 'green' },
  {
    name: 'Gefühlschaos',
    dim: 'Emotionswahrnehmung',
    kind: 'dot',
    color: 'pink'
  },
  {
    name: 'Fliegender Händler',
    dim: 'Risikobereitschaft',
    kind: 'tri',
    color: 'yellow'
  }
]

export function GamePanel() {
  const { inB } = useBeats()
  return (
    <Panel
      at={inB('games', 0.16, 0.3)}
      out={inB('games', 0.9, 1)}
      className='off-games'
    >
      <div className='off-doc__eyebrow'>Warum game-based?</div>
      {BENEFITS.map(({ Icon, title, text }, i) => (
        <div
          key={title}
          className='off-benefit'
          style={anim({
            at: inB('games', 0.24 + i * 0.08, 0.36 + i * 0.08),
            x: 30,
            y: 0
          })}
        >
          <span className='off-benefit__icon'>
            <Icon width={20} height={20} />
          </span>
          <div>
            <strong>{title}</strong>
            <p>{text}</p>
          </div>
        </div>
      ))}
      <div
        className='off-games__list'
        style={anim({ at: inB('games', 0.5, 0.62), y: 16 })}
      >
        {GAMES.map((g) => (
          <span key={g.name} className='off-game'>
            <Shape kind={g.kind} color={g.color} size={14} />
            <b>{g.name}</b>
            <small>{g.dim}</small>
          </span>
        ))}
      </div>
    </Panel>
  )
}

// ---------- 7 · Ein Blick in Aivy (hängt im Würfel) ----------

const STEPS = [
  {
    n: 1,
    title: 'Spielverhalten',
    text: 'Jede Entscheidung im Spiel ist ein Datenpunkt.'
  },
  {
    n: 2,
    title: 'Validierte Dimensionen',
    text: 'Daraus entstehen wissenschaftlich validierte Werte.'
  },
  {
    n: 3,
    title: 'Abgleich',
    text: 'Jede Dimension trifft auf Ideal- und Übergangsbereich.'
  },
  { n: 4, title: 'Passung', text: 'Feste Regeln ergeben den Match-Score.' }
]

export function MagicPanel() {
  const { inB } = useBeats()
  return (
    <div
      className='off-magic'
      style={anim({
        at: inB('magic', 0.12, 0.24),
        out: inB('magic', 0.9, 1),
        s: 0.4,
        y: 0
      })}
    >
      <div className='off-magic__title'>So rechnet Aivy</div>
      <div className='off-magic__steps'>
        {STEPS.map((st, i) => (
          <div
            key={st.n}
            className='off-step'
            style={anim({
              at: inB('magic', 0.24 + i * 0.14, 0.34 + i * 0.14),
              y: 16,
              s: 0.8
            })}
          >
            <span className='off-step__n'>{st.n}</span>
            <div className='off-step__visual'>
              {st.n === 1 && (
                <div className='off-step__shapes'>
                  <Shape kind='tri' color='yellow' size={14} />
                  <Shape kind='sq' color='green' size={12} />
                  <Shape kind='dot' color='pink' size={12} />
                </div>
              )}
              {st.n === 2 && (
                <div className='off-step__bars'>
                  {[70, 45, 85].map((v, j) => (
                    <i key={j} style={{ width: `${v}%` }} />
                  ))}
                </div>
              )}
              {st.n === 3 && (
                <Beam ideal={[55, 80]} trans={[45, 90]} value={72} />
              )}
              {st.n === 4 && <strong className='off-step__score'>91</strong>}
            </div>
            <strong>{st.title}</strong>
            <p>{st.text}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------- 8 · Regelbasiert ----------

const RULES = [
  {
    Icon: ScaleIcon,
    title: 'Regelbasiert',
    text: 'Die Passung folgt festen, transparenten Regeln. Keine Blackbox bewertet Menschen.'
  },
  {
    Icon: DocumentTextIcon,
    title: 'Nachvollziehbar',
    text: 'Jedes Ergebnis lässt sich bis auf die einzelne Dimension erklären und prüfen.'
  },
  {
    Icon: ShieldCheckIcon,
    title: 'AI-Act-ready',
    text: 'Gebaut für die Anforderungen an KI im Recruiting, mit dem Menschen in der Entscheidung.'
  }
]

export function RulePanel() {
  const { inB } = useBeats()
  return (
    <Panel
      place='center'
      at={inB('regeln', 0.06, 0.24)}
      out={inB('regeln', 0.9, 1)}
      className='off-rules'
    >
      <div className='off-rules__badge'>
        <LockClosedIcon width={16} height={16} /> Im Kern ohne KI
      </div>
      <h3 className='off-rules__title'>
        Regelbasiert. Nachvollziehbar. <span>AI&#8209;Act&#8209;ready.</span>
      </h3>
      <div className='off-rules__grid'>
        {RULES.map(({ Icon, title, text }, i) => (
          <div
            key={title}
            className='off-rule'
            style={anim({
              at: inB('regeln', 0.22 + i * 0.1, 0.36 + i * 0.1),
              y: 24
            })}
          >
            <Icon width={26} height={26} />
            <strong>{title}</strong>
            <p>{text}</p>
          </div>
        ))}
      </div>
    </Panel>
  )
}

// ---------- 9 · Bewerbende bekommen ihr Stärkenprofil ----------

export function TalentPhone() {
  const { inB } = useBeats()
  return (
    <div
      className='off-talent'
      style={anim({
        at: inB('talente', 0.12, 0.26),
        out: inB('talente', 0.9, 1),
        y: 30,
        s: 0.6
      })}
    >
      <div className='off-talent__phone'>
        <div className='off-talent__notch' />
        <div className='off-talent__eyebrow'>Komplett kostenlos</div>
        <div className='off-talent__title'>Dein Stärkenprofil</div>
        {STRENGTHS.map((s, i) => (
          <div
            key={s.title}
            className='off-talent__row'
            style={anim({
              at: inB('talente', 0.26 + i * 0.08, 0.36 + i * 0.08),
              y: 12
            })}
          >
            <Shape kind={s.shape} color={s.color} size={16} />
            <div>
              <b>{s.title}</b>
              <small>{s.text}</small>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------- 10 · Passungsprofil als PDF, Assistent als Helfer ----------

const FITS = [
  { cat: 'Fähigkeiten', value: 93 },
  { cat: 'Persönlichkeit', value: 88 },
  { cat: 'Kultur', value: 92 },
  { cat: 'Berufliche Interessen', value: 86 }
]

export function PdfPanel() {
  const { inB } = useBeats()
  return (
    <Panel
      at={inB('report', 0.3, 0.44)}
      out={inB('report', 0.92, 1)}
      className='off-pdf'
    >
      <div className='off-pdf__page'>
        <div className='off-pdf__top'>
          <span className='off-pdf__file'>
            <DocumentTextIcon width={14} height={14} /> PDF
          </span>
          <span>Passungsprofil</span>
        </div>
        <div className='off-pdf__name'>
          Lena K. · Customer Success Manager:in
        </div>
        <div className='off-glance'>
          <div className='off-glance__score'>
            <Count to={91} at={inB('report', 0.4, 0.56)} />
            <i>
              <b style={{ width: '91%' }} />
            </i>
          </div>
          <div>
            <strong>Hohe Passung</strong>
            <p>
              Lena erfüllt alle Muss-Kriterien und liegt in den meisten
              Dimensionen im Idealbereich.
            </p>
          </div>
        </div>
        <div className='off-fits'>
          {FITS.map((f, i) => (
            <div
              key={f.cat}
              className='off-fit'
              style={anim({
                at: inB('report', 0.46 + i * 0.04, 0.56 + i * 0.04),
                y: 12
              })}
            >
              <span>{f.cat}</span>
              <b>{f.value}</b>
            </div>
          ))}
        </div>
        <div className='off-pdf__sections'>
          {['Chancen', 'Risiken', 'Interviewfragen'].map((t) => (
            <div key={t}>
              <strong>{t}</strong>
              <i />
              <i />
            </div>
          ))}
        </div>
      </div>
      <div
        className='off-helper'
        style={anim({ at: inB('report', 0.62, 0.74), x: 30, y: 0, s: 0.8 })}
      >
        <div className='off-helper__head'>
          <span>
            <SparklesIcon width={12} height={12} color='#fff' />
          </span>
          Aivy Assistent
          <small>Hilfsmittel</small>
        </div>
        <Typed
          text='Kurz gesagt: Lena passt besonders bei Emotionswahrnehmung und Teamarbeit. Details stehen im Passungsprofil.'
          at={inB('report', 0.72, 0.9)}
        />
      </div>
    </Panel>
  )
}

// ---------- 11 · Interviewfragen aus dem Passungsprofil ----------

export function InterviewCard() {
  const { inB } = useBeats()
  return (
    <div
      className='off-interview'
      style={anim({
        at: inB('gespraech', 0.5, 0.6),
        out: inB('gespraech', 0.9, 1),
        y: 20,
        s: 0.7
      })}
    >
      <div className='off-interview__head'>
        <ChatBubbleLeftRightIcon width={13} height={13} /> Interviewfragen aus
        dem Passungsprofil
      </div>
      <ol>
        <li>
          Erzähl von einem Kunden, der unzufrieden war. Was hast du getan?
        </li>
        <li>Wann arbeitest du lieber allein, wann im Team?</li>
      </ol>
    </div>
  )
}

// ---------- 13 · Faire Absagen ----------

const FAIR = [
  'Gleiche Kriterien für alle Bewerbenden',
  'Jede Entscheidung datenbasiert und begründbar',
  'Mehr rechtliche Sicherheit für HR',
  'Alle bekommen ihr Stärkenprofil'
]

export function FairPanel() {
  const { inB } = useBeats()
  return (
    <Panel
      at={inB('absagen', 0.18, 0.32)}
      out={inB('absagen', 0.9, 1)}
      className='off-fair'
    >
      <div className='off-doc__eyebrow'>Absagen mit gutem Gewissen</div>
      <h3 className='off-fair__title'>Datenbasiert. Fair für alle.</h3>
      {FAIR.map((t, i) => (
        <div
          key={t}
          className='off-fair__row'
          style={anim({
            at: inB('absagen', 0.3 + i * 0.08, 0.4 + i * 0.08),
            x: 24,
            y: 0
          })}
        >
          <span>
            <CheckIcon width={14} height={14} />
          </span>
          {t}
        </div>
      ))}
    </Panel>
  )
}

// ---------- 14 · Im eigenen ATS mit Sidebar-Plugin ----------

const ATS = [
  'softgarden',
  'Personio',
  'Workday',
  'Teamtailor',
  'SmartRecruiters',
  'd.vinci'
]

export function AtsPanel() {
  const { inB } = useBeats()
  return (
    <Panel
      place='center'
      at={inB('ats', 0.08, 0.26)}
      out={inB('ats', 0.9, 1)}
      className='off-ats'
    >
      <div className='off-ats__window'>
        <div className='off-ats__bar'>
          <i />
          <i />
          <i />
          <span>Dein ATS</span>
        </div>
        <div className='off-ats__body'>
          <div className='off-ats__main'>
            <div className='off-ats__h'>
              Customer Success Manager:in · Bewerbungen
            </div>
            {['Lena K.', 'Aylin S.', 'Jonas M.', 'Tom B.'].map((n, i) => (
              <div key={n} className={clsx('off-ats__row', i === 0 && 'is-on')}>
                <Avatar
                  initials={n[0]}
                  color={i === 0 ? 'pink' : '#B2B4B6'}
                  size={20}
                />
                <span>{n}</span>
                <i />
              </div>
            ))}
          </div>
          <div
            className='off-ats__side'
            style={anim({ at: inB('ats', 0.3, 0.46), x: 60, y: 0 })}
          >
            <div className='off-ats__brand'>
              <span>aivy</span> Sidebar-Plugin
            </div>
            <div className='off-ats__score'>
              <b>91</b> Hohe Passung
            </div>
            {PROFILE.slice(0, 3).map((d) => (
              <div key={d.name} className='off-ats__dim'>
                <small>{d.name}</small>
                <Beam ideal={d.ideal} trans={d.trans} value={d.ideal[0] + 10} />
              </div>
            ))}
            <div className='off-ats__ask'>
              <SparklesIcon width={12} height={12} /> Frag den Aivy Assistenten
              …
            </div>
          </div>
        </div>
      </div>
      <div
        className='off-ats__partners'
        style={anim({ at: inB('ats', 0.5, 0.64), y: 12 })}
      >
        {ATS.map((a) => (
          <span key={a}>{a}</span>
        ))}
        <span>u.v.m.</span>
      </div>
    </Panel>
  )
}

// ---------- 15 · Support ----------

export function SupportPanel() {
  const { inB } = useBeats()
  return (
    <Panel
      at={inB('support', 0.1, 0.3)}
      out={inB('support', 0.9, 1)}
      className='off-support'
    >
      <div className='off-support__call'>
        <div className='off-support__video'>
          <span className='off-support__face'>
            <UserGroupIcon width={34} height={34} />
          </span>
          <span className='off-support__live'>Aivy Support</span>
        </div>
        <div className='off-support__text'>
          <strong>Wir helfen immer gern.</strong>
          <p>
            Fragen, Feedback oder ein kurzer Call. Unser Team ist für dich da.
          </p>
          <span className='off-chip off-chip--blue'>help@aivy.app</span>
        </div>
      </div>
    </Panel>
  )
}
